/**
 * 马达校准 + ADC 行程的隐藏页。
 * 路径是带过期时间的动态密文：#/p/<token>，可分享，24 小时后失效。
 * 不进侧栏。签发后把完整链接复制给对方即可，对方不用知道快捷键。
 */

const STORAGE_KEY = 'mouse-driver.rtLabRoute';
const PURPOSE_PREFIX = 'rt-lab.v1';
const NONCE_LENGTH = 12;

export const RT_LAB_TTL_MS = 24 * 60 * 60 * 1000;
export const RT_LAB_TTL_LABEL = '24 小时';

// CRA 5 的 eslint 环境还是 es6，不认 globalThis；与下面 navigator/document 一样按运行时探测。
const getRuntimeGlobal = () =>
  typeof window !== 'undefined' ? window : undefined;

const toCharCodes = (text) =>
  Uint8Array.from([...text], (ch) => ch.charCodeAt(0));

const xorWithNonce = (payload, nonce) =>
  payload.map((byte, index) => byte ^ nonce[index % nonce.length]);

const bytesToBase64Url = (bytes) => {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const base64UrlToBytes = (text) => {
  const padded = text.replace(/-/g, '+').replace(/_/g, '/');
  const pad =
    padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  const binary = atob(padded + pad);
  return Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
};

const randomBytes = (length) => {
  const bytes = new Uint8Array(length);
  const cryptoApi = getRuntimeGlobal()?.crypto;
  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(bytes);
    return bytes;
  }
  for (let i = 0; i < length; i += 1) {
    bytes[i] = Math.floor(Math.random() * 256);
  }
  return bytes;
};

const readIssuedToken = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeIssuedToken = (token) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // 隐私模式写不进 sessionStorage 时，快捷键每次会再签发一枚
  }
};

const decryptToken = (token) => {
  const bytes = base64UrlToBytes(token);
  if (bytes.length <= NONCE_LENGTH) return null;
  const nonce = bytes.slice(0, NONCE_LENGTH);
  const cipher = bytes.slice(NONCE_LENGTH);
  return String.fromCharCode(...xorWithNonce(cipher, nonce));
};

const encodePayload = (expiresAt) => `${PURPOSE_PREFIX}:${expiresAt}`;

const parsePayload = (plain) => {
  if (!plain || !plain.startsWith(`${PURPOSE_PREFIX}:`)) return null;
  const expiresAt = Number(plain.slice(PURPOSE_PREFIX.length + 1));
  if (!Number.isFinite(expiresAt)) return null;
  return { expiresAt };
};

export function readRtLabToken(token, now = Date.now()) {
  if (!token || typeof token !== 'string') {
    return { valid: false, expired: false, expiresAt: null };
  }
  try {
    const parsed = parsePayload(decryptToken(token));
    if (!parsed) return { valid: false, expired: false, expiresAt: null };
    if (now >= parsed.expiresAt) {
      return { valid: false, expired: true, expiresAt: parsed.expiresAt };
    }
    return { valid: true, expired: false, expiresAt: parsed.expiresAt };
  } catch {
    return { valid: false, expired: false, expiresAt: null };
  }
}

/** 签发一枚新密文（nonce 随机，并写入 24 小时过期时间） */
export function issueRtLabToken({ now = Date.now(), ttlMs = RT_LAB_TTL_MS } = {}) {
  const expiresAt = now + ttlMs;
  const nonce = randomBytes(NONCE_LENGTH);
  const cipher = xorWithNonce(toCharCodes(encodePayload(expiresAt)), nonce);
  const packed = new Uint8Array(NONCE_LENGTH + cipher.length);
  packed.set(nonce, 0);
  packed.set(cipher, NONCE_LENGTH);
  const token = bytesToBase64Url(packed);
  writeIssuedToken(token);
  return token;
}

export function getRtLabToken(now = Date.now()) {
  const existing = readIssuedToken();
  if (existing && isRtLabToken(existing, now)) return existing;
  return issueRtLabToken({ now });
}

/** 隐藏路径，例如 /p/xY8_...；本机有未过期缓存时复用 */
export function ensureRtLabPath(now = Date.now()) {
  return `/p/${getRtLabToken(now)}`;
}

export function buildRtLabShareUrl(path, location = getRuntimeGlobal()?.location) {
  if (!location) return path;
  return `${location.origin}${location.pathname}${location.search}#${path}`;
}

/** 新签发一枚 24 小时临时链接，给对方直接打开 */
export function issueRtLabShare({
  now = Date.now(),
  ttlMs = RT_LAB_TTL_MS,
  location = getRuntimeGlobal()?.location,
} = {}) {
  const token = issueRtLabToken({ now, ttlMs });
  const path = `/p/${token}`;
  return {
    token,
    path,
    url: buildRtLabShareUrl(path, location),
    expiresAt: now + ttlMs,
    ttlMs,
  };
}

export async function copyRtLabShare(options) {
  const share = issueRtLabShare(options);
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(share.url);
    return share;
  }
  if (typeof document !== 'undefined') {
    const area = document.createElement('textarea');
    area.value = share.url;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.left = '-9999px';
    document.body.appendChild(area);
    area.select();
    document.execCommand('copy');
    area.remove();
  }
  return share;
}

export function isRtLabPathname(pathname = '', now = Date.now()) {
  const raw = String(pathname).replace(/^\//, '');
  if (!raw.startsWith('p/')) return false;
  return isRtLabToken(raw.slice(2), now);
}

export function readRtLabPathname(pathname = '', now = Date.now()) {
  const raw = String(pathname).replace(/^\//, '');
  if (!raw.startsWith('p/')) {
    return { valid: false, expired: false, expiresAt: null };
  }
  return readRtLabToken(raw.slice(2), now);
}

export function isRtLabToken(token, now = Date.now()) {
  return readRtLabToken(token, now).valid;
}

export function formatRtLabRemain(expiresAt, now = Date.now()) {
  const remain = Math.max(0, Number(expiresAt) - now);
  const hours = Math.ceil(remain / 3600000);
  if (hours >= 1) return `${hours} 小时`;
  const minutes = Math.max(1, Math.ceil(remain / 60000));
  return `${minutes} 分钟`;
}

export function resetRtLabRoute() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
